import {User} from './user';
import {Document} from './document';
import {PartnerAccountType} from '../constants/partner-account-type';
import {PartnerStatus} from '../constants/partner-status';
import {PartnerContact} from './partner-contact';
import {ProspectOrigin} from '../constants/prospect-origin';
import {PartnerTypology} from '../constants/partner-typology';
import {GeographicArea} from '../constants/geographic-area';
import {PartnerExperience} from '../constants/partner-experience';
import {PaymentTerms} from '../constants/payment-terms';
import {PaymentMethod} from '../constants/payment-method';
import {CompanyRole} from '../constants/company-role';
import {CompanyStatus} from '../constants/company-status';
import {AbstractDocumentsModel} from './abstract-documents-model';

export class Partner extends AbstractDocumentsModel {
  accountManagerId: number | null = null;
  accountManager: User | null = null;
  accountType: PartnerAccountType | null = null;
  status: PartnerStatus | null = null;
  advertisingPlatform: string | null = null;
  prospect: boolean = false;
  companyStatus: CompanyStatus | null = null;
  companyName: string | null = null;
  companyRegistrationNumber: string | null = null;
  companyVatNumber: string | null = null;
  langs: string | null = null;
  country: string | null = null;
  address: string | null = null;
  zipCode: string | null = null;
  state: string | null = null;
  city: string | null = null;
  email: string | null = null;
  phoneNumber: string | null = null;
  prospectOrigin: ProspectOrigin | null = null;
  typology: PartnerTypology | null = null;
  verticals: number[] = [];
  geographicAreas: GeographicArea[] = [];
  trafficSources: string | null = null;
  experience: PartnerExperience | null = null;
  monthlyEstimateRevenue: number | null = null;
  websiteUrl: string | null = null;
  paymentThreshold: number | null = null;
  paymentTerms: PaymentTerms = PaymentTerms.NET30;
  paymentCurrency: string = 'EUR';
  paymentMethod: PaymentMethod = PaymentMethod.WIRE;
  accountHolder: string | null = null;
  bankName: string | null = null;
  bankAddress: string | null = null;
  iban: string | null = null;
  swift: string | null = null;
  routingNumber: string | null = null;

  credentialUrl!: string;
  credentialUsername!: string;
  credentialPassword!: string;

  image: Document | null = null;

  lastActivityAt: Date | null = null;

  allOffersAccess: boolean = true;

  contacts: Record<CompanyRole, PartnerContact> = {
    [CompanyRole.MAIN]: new PartnerContact(),
    [CompanyRole.SIGNATORY]: new PartnerContact(),
    [CompanyRole.SALES]: new PartnerContact(),
    [CompanyRole.TECHNICAL]: new PartnerContact(),
    [CompanyRole.ACCOUNTING]: new PartnerContact(),
  };

  override deserialize(input: any): this {

    super.deserialize(input);

    if (input.accountManager) {
      this.accountManager = new User().deserialize(input.accountManager);
    }

    if (input.image) {
      this.image = new Document().deserialize(input.image);
    }

    if (input.contacts) {
      this.contacts = input.contacts.reduce((acc: Record<CompanyRole, PartnerContact>, contact: any) => {
        const partnerContact = new PartnerContact().deserialize(contact);
        if (partnerContact.companyRole) {
          acc[partnerContact.companyRole] = partnerContact;
        }
        return acc;
      }, this.contacts);
    }

    return this;
  }

  getMainContact(): PartnerContact {
    return this.contacts[CompanyRole.MAIN];
  }

  isAffiliate(): boolean {
    return this.accountType === PartnerAccountType.AFFILIATE;
  }

  isMediaBuy(): boolean {
    return this.accountType === PartnerAccountType.MEDIABUY;
  }

  getAccountTypeName(): string | null {
    switch (this.accountType) {
      case PartnerAccountType.AFFILIATE:
        return 'Affiliate';
      case PartnerAccountType.MEDIABUY:
        return 'Media Buy';
      case PartnerAccountType.INTERNAL:
        return 'Internal';
    }

    return null;
  }

  getAccountTypeClass(): string | null {
    switch (this.accountType) {
      case PartnerAccountType.AFFILIATE:
        return 'bg-primary';
      case PartnerAccountType.MEDIABUY:
        return 'bg-info';
      case PartnerAccountType.INTERNAL:
        return 'bg-secondary';
    }

    return null;
  }

  getStateName(): string | null {
    switch (this.status) {
      case PartnerStatus.ACTIVE:
        return 'Active';
      case PartnerStatus.REJECTED:
        return 'Rejected';
      case PartnerStatus.PENDING:
        return 'Pending';
      case PartnerStatus.SUSPENDED:
        return 'Suspended';
      case PartnerStatus.CLOSED:
        return 'Closed';
    }

    return null;
  }

  getStateClass(): string | null {
    switch (this.status) {
      case PartnerStatus.ACTIVE:
        return 'bg-success';
      case PartnerStatus.REJECTED:
        return 'bg-danger';
      case PartnerStatus.PENDING:
        return 'bg-warning';
      case PartnerStatus.SUSPENDED:
        return 'bg-light text-dark';
      case PartnerStatus.CLOSED:
        return 'bg-dark';
    }

    return null;
  }

  override toString(): string {
    let name = '';

    if (this.companyName) {
      name += this.companyName;
    }

    name += ' #' + this.id;

    return name.trim();
  }
}
